/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { autorun } from 'mobx';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getEventBus } from 'src/core/utils/eventBus';
import { ToastData } from 'src/luis/core/stores/ToastStore';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';
import { TrainingProgressIndicator } from 'src/modules/modelTraining/components/TrainingProgressIndicator';
import { TrainingJobStore } from 'src/modules/modelTraining/stores/TrainingJobInfoStore';

const eventBus = getEventBus();
const messages = defineMessages({
    cancel: { id: 'useModelTrainingTracker.cancel', defaultMessage: 'Training cancelled' },
    canceling: { id: 'useModelTrainingTracker.canceling', defaultMessage: 'Training cancelling...' },
    failedDetails: { id: 'useModelTrainingTracker.failedDetails', defaultMessage: 'Training failed' },
    failTitle: { id: 'useModelTrainingTracker.failTitle', defaultMessage: "'{ modelName }' train failed" },
    errorDetails: { id: 'useModelTrainingTracker.errorDetails', defaultMessage: 'Click here for more details.' },
    cancelTitle: { id: 'useModelTrainingTracker.cancelTitle', defaultMessage: "'{ modelName }' training cancelled" },
    successTitle: { id: 'useModelTrainingTracker.successTitle', defaultMessage: "'{ modelName }' training completed" },
    cancelingTitle: { id: 'useModelTrainingTracker.cancelingTitle', defaultMessage: "'{ modelName }' training cancelling" },
    success: { id: 'useModelTrainingTracker.success', defaultMessage: 'Finished training. Your changes are now effective.' },
    inProgressTitle: { id: 'useModelTrainingTracker.inProgressTitle', defaultMessage: "'{ modelName }' training in progress" }
});

const getToastDataFromJob = ({
    localize,
    trainingJob,
    onTrainErrorClick
}: {
    trainingJob: TrainingJobStore;
    onTrainErrorClick: (jobId: string) => void;
    localize: ReactIntl.InjectedIntl['formatMessage'];
}): ToastData => {
    switch (trainingJob.status) {
        case 'notStarted':
        case 'inProgress':
            return {
                status: 'running',
                title: localize(messages.inProgressTitle, { modelName: trainingJob.modelId }),
                content: <TrainingProgressIndicator trainingJob={trainingJob} />
            };
        case 'succeeded':
            return {
                status: 'completed',
                content: localize(messages.success),
                title: localize(messages.successTitle, { modelName: trainingJob.modelId })
            };
        case 'cancelled':
            return {
                status: 'informative',
                content: localize(messages.cancel),
                title: localize(messages.cancelTitle, { modelName: trainingJob.modelId })
            };
        case 'cancelling':
            return {
                status: 'informative',
                content: localize(messages.canceling),
                title: localize(messages.cancelingTitle, { modelName: trainingJob.modelId })
            };
        case 'failed':
            return {
                status: 'failed',
                content: localize(messages.failedDetails),
                title: localize(messages.failTitle, { modelName: trainingJob.modelId }),
                actions: [{ label: localize(messages.errorDetails), onClick: () => onTrainErrorClick(trainingJob.id) }]
            };
        default:
            return {};
    }
};

export const useModelTrainingTracker = (
    trainingJobStoreHouse: ResourceStoreHouse<TrainingJobStore>,
    onTrainErrorClick: (jobId: string) => void
) => {
    const { localize } = useLocalization();
    const toastIdRef = React.useRef<string>();
    const { rootStore, dispatcher } = useGlobalState();

    React.useEffect(() => {
        if (!trainingJobStoreHouse) {
            return;
        }

        const disposer = autorun(() => {
            const toastTrainingJob = trainingJobStoreHouse.items.find(j => j.id === toastIdRef.current);
            const runningTrainingJob = trainingJobStoreHouse.items.find(j => j.status === 'notStarted' || j.status === 'inProgress');

            if (!runningTrainingJob && !toastTrainingJob) {
                return;
            }

            const currentTrainingJob = runningTrainingJob ?? toastTrainingJob;

            const toastData = getToastDataFromJob({
                localize,
                onTrainErrorClick,
                trainingJob: currentTrainingJob
            });

            if (toastIdRef.current !== currentTrainingJob.id) {
                toastIdRef.current = currentTrainingJob.id;
                eventBus.emit('addToast', { id: toastIdRef.current, ...toastData });
            } else {
                eventBus.emit('updateToast', { id: toastIdRef.current, toast: toastData });

                if (
                    toastTrainingJob.status === 'succeeded' &&
                    rootStore.currentCustomTextProjectStore?.projectKind === 'CustomEntityRecognition'
                ) {
                    dispatcher.dispatch('getExtractionExportProject', { projectName: rootStore.currentCustomTextProjectStore.name });
                }
            }
        });

        return () => {
            disposer();
            if (rootStore.toastStoreHouse.findById(toastIdRef.current)) {
                dispatcher.dispatch('removeToast', { id: toastIdRef.current });
            }
        };
    }, [trainingJobStoreHouse]);
};
